'use client';

import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useGetAdvancedSearchMetricsQuery } from 'src/api/redux/advancedSearches';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import { useHeatmapMetrics } from 'src/hooks/heatmap';
import type { FlatHeatmapMetric } from 'src/hooks/heatmap';
import {
  selectDiscoveryMapHeatMap,
  selectDiscoveryMapSelectedHeatmapMetric,
  setHeatMap,
  setSelectedHeatmapMetric,
} from 'src/redux/slices/discoveryMapSlice';
import { HeatmapMetricList } from 'src/components/HeatmapMetricList';
import { CircularProgress } from 'src/components';

interface HeatmapMetricPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

const HeatmapMetricPopover: React.FC<HeatmapMetricPopoverProps> = ({
  isOpen,
  onClose,
  anchorRef,
}) => {
  const dispatch = useDispatch();
  const popoverRef = useRef<HTMLDivElement>(null);

  const heatMap = useSelector(selectDiscoveryMapHeatMap);
  const selectedHeatmapMetric = useSelector(
    selectDiscoveryMapSelectedHeatmapMetric
  );

  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const advancedSearchId =
    appliedSavedVolume?.volumeType === 'advancedSearch'
      ? appliedSavedVolume?.id
      : undefined;

  const { data: metricsData, isLoading: isMetricsLoading } =
    useGetAdvancedSearchMetricsQuery(advancedSearchId, {
      skip: !advancedSearchId,
    });

  const handleMetricChange = useCallback(
    (metric: { code: string; label: string } | null) => {
      dispatch(setSelectedHeatmapMetric(metric));
    },
    [dispatch]
  );

  const { groupedItems, requestCodeFor } = useHeatmapMetrics({
    metrics: metricsData?.results,
    selectedMetric: selectedHeatmapMetric,
    onMetricChange: handleMetricChange,
  });

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  const handleSelectAllOpportunities = () => {
    dispatch(setHeatMap(true));
    dispatch(setSelectedHeatmapMetric(null));
    onClose();
  };

  const handleSelectMetric = (opt: FlatHeatmapMetric) => {
    const requestCode = requestCodeFor(opt.code);
    const isCurrentlySelected = selectedHeatmapMetric?.code === requestCode;
    if (isCurrentlySelected) {
      dispatch(setSelectedHeatmapMetric(null));
      dispatch(setHeatMap(false));
    } else {
      dispatch(
        setSelectedHeatmapMetric({ code: requestCode, label: opt.label })
      );
      dispatch(setHeatMap(true));
    }
    onClose();
  };

  const handleTurnOff = () => {
    dispatch(setHeatMap(false));
    dispatch(setSelectedHeatmapMetric(null));
    onClose();
  };

  if (!isOpen) return null;

  // Position above and left-aligned with the anchor button
  const anchorRect = anchorRef.current?.getBoundingClientRect();
  if (!anchorRect) return null;

  // Dynamic positioning requires inline style — Tailwind cannot compute
  // runtime pixel values from getBoundingClientRect(). This follows the
  // same pattern as PulseUI/Dropdown (lines 128-132).
  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: window.innerHeight - anchorRect.top + 8,
    left: anchorRect.left,
    zIndex: 1000,
  };

  return createPortal(
    <div
      ref={popoverRef}
      data-testid="heatmap-metric-popover"
      className="bg-white rounded-lg shadow-lg outline outline-1 outline-neutral-200 w-64 max-h-80 overflow-y-auto"
      style={popoverStyle}
      aria-label="Heatmap metric selection"
    >
      {isMetricsLoading ? (
        <div
          className="flex items-center justify-center py-8"
          role="status"
          aria-label="Loading metrics"
          data-testid="heatmap-metrics-loading"
        >
          <CircularProgress size="sm" color="primary" />
        </div>
      ) : (
      <HeatmapMetricList
        groupedItems={groupedItems}
        selectedMetricCode={selectedHeatmapMetric?.code}
        showHeatmap={heatMap}
        requestCodeFor={requestCodeFor}
        onSelectAllOpportunities={handleSelectAllOpportunities}
        onSelectMetric={handleSelectMetric}
        onTurnOff={handleTurnOff}
      />
      )}
    </div>,
    document.body
  );
};

export default HeatmapMetricPopover;
